import { useNavigate } from 'react-router-dom';
import { BarChart3, Plus, UserPlus, Wallet, type LucideIcon } from 'lucide-react';
import { prefetchScreensList } from '@/features/billboards/lib/prefetchScreensList';
import { useAppSelector } from '@/shared/store/hooks';
import {
  isAgencyRole,
  isClientRole,
  isTeamMemberRole,
} from '@/shared/config/roles';

type QuickActionCard = {
  title: string;
  desc: string;
  btn: string;
  path: string;
  icon: LucideIcon;
  chipBg: string;
  chipColor: string;
  btnBg: string;
  agencyOnly?: boolean;
  hideForClient?: boolean;
  hideForTeamMember?: boolean;
};

const ACTIONS: QuickActionCard[] = [
  {
    title: 'Create New Campaign',
    desc: 'Launch in 3 easy steps',
    btn: 'Create Campaign',
    path: '/campaigns',
    icon: Plus,
    chipBg: '#E9E2F7',
    chipColor: '#5B3A9E',
    btnBg: 'linear-gradient(135deg, #3B66B8 0%, #8156B4 100%)',
    hideForClient: true,
  },
  {
    title: 'Create Client',
    desc: 'Add a new client account',
    btn: 'Create Client',
    path: '/clients/add',
    icon: UserPlus,
    chipBg: '#EDE4F8',
    chipColor: '#8156B4',
    btnBg: 'linear-gradient(135deg, #8156B4 0%, #3B66B8 100%)',
    agencyOnly: true,
  },
  {
    title: 'Campaign Performance',
    desc: 'View live campaign metrics',
    btn: 'View Analytics',
    path: '/total-analytics',
    icon: BarChart3,
    chipBg: '#DDF5EA',
    chipColor: '#12785A',
    btnBg: 'linear-gradient(135deg, #12785A 0%, #1E9B6E 100%)',
  },
  {
    title: 'Add Funds',
    desc: 'Recharge your wallet',
    btn: 'Add Funds',
    path: '/wallet',
    icon: Wallet,
    chipBg: '#DFE9FA',
    chipColor: '#3B66B8',
    btnBg: 'linear-gradient(180deg, #5F69A8 0%, #474B8F 100%)',
    hideForClient: true,
    hideForTeamMember: true,
  },
];

export function DashboardQuickActions() {
  const navigate = useNavigate();
  const storeCity = useAppSelector((s) => s.filters.city);
  const role = useAppSelector((s) => s.auth.user?.role);
  const isAgency = isAgencyRole(role);
  const isClient = isClientRole(role);
  const isTeamMember = isTeamMemberRole(role);

  const cards = ACTIONS.filter((c) => {
    if (c.agencyOnly && !isAgency) return false;
    if (c.hideForClient && isClient) return false;
    if (c.hideForTeamMember && isTeamMember) return false;
    return true;
  });

  return (
    <div
      className={`grid grid-cols-1 gap-5 sm:grid-cols-2 ${
        cards.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
      }`}
    >
      {cards.map((card) => (
        <div
          key={card.title}
          onMouseEnter={
            card.path === '/screens'
              ? () => void prefetchScreensList(storeCity)
              : undefined
          }
          className="flex flex-col rounded-[24px] bg-white p-[22px] text-left shadow-[0_10px_30px_rgba(60,40,120,0.05)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_36px_rgba(60,40,120,0.09)]"
        >
        
          <div
            className="inline-flex h-10 w-10 items-center justify-center rounded-[12px]"
            style={{ background: card.chipBg, color: card.chipColor }}
          >
            <card.icon size={18} strokeWidth={2} />
          </div>
          <h3 className="m-0 mt-4 text-[17px] font-extrabold text-[#3A3660]">
            {card.title}
          </h3>
          <p className="mt-1 mb-5 text-[13px] leading-snug text-[#6A6688]">
            {card.desc}
          </p>
          <button
            type="button"
            onClick={() => {
              if (card.title === 'Create New Campaign') {
                navigate('/campaigns', { state: { openCampaignCreate: true } });
                return;
              }
              navigate(card.path);
            }}
            className="mt-auto flex h-[48px] w-full items-center justify-center rounded-[14px] border-none text-[14px] font-bold text-white transition hover:brightness-105 active:scale-[0.99]"
            style={{ background: card.btnBg }}
          >
            {card.btn}
          </button>
        </div>
      ))}
    </div>
  );
}
